import { useState } from 'react';
import { API_URL } from '../../config/api';

const AttachCustomerModal = ({ isOpen, onClose, orderNo, onSuccess }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isAttaching, setIsAttaching] = useState(false);

  if (!isOpen) return null;

  const handleSearch = async (term) => {
    setSearch(term);
    if (term.length < 2) {
      setResults([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/customers/search?term=${encodeURIComponent(term)}`);
      if (!response.ok) throw new Error(`Server responded with ${response.status}`);
      const data = await response.json();
      setResults(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error searching customers:', error);
      setResults([]);
    }
  };

  const handleSelect = (customer) => {
    const fullName = customer.CustomerFullName ||
      `${customer.CustomerFirstName || ''} ${customer.CustomerLastName || ''}`.trim() ||
      'Unknown Customer';
    setSelected({
      ID: customer.ID,
      customerName: fullName,
      phone: customer.CustomerPhone || '',
      email: customer.CustomerEmail || '',
      address: customer.CustomerAddress || '',
      city: customer.CustomerCity || '',
      state: customer.CustomerState || '',
      zip: customer.CustomerZipCode || '',
      moldId: customer.MoldId || ''
    });
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    if (!selected || !orderNo) return;
    setIsAttaching(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/attach-customer`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderNo,
          customerNo: selected.ID,
          customerName: selected.customerName,
          customerPhone: selected.phone,
          customerEmail: selected.email,
          customerAddress: selected.address || '',
          customerCity: selected.city || '',
          customerState: selected.state || '',
          customerZipCode: selected.zip || '',
          moldId: selected.moldId || ''
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Server responded with ${response.status}`);
      }
      handleClose();
      await onSuccess();
      alert(`Successfully attached ${selected.customerName} to order ${orderNo}`);
    } catch (error) {
      console.error('Error attaching customer:', error);
      alert(`Error attaching customer: ${error.message}`);
    } finally {
      setIsAttaching(false);
    }
  };

  const handleClose = () => {
    setSearch('');
    setResults([]);
    setSelected(null);
    setShowConfirmation(false);
    document.body.style.overflow = 'unset';
    onClose();
  };

  return (
    <div className="modal fade show attach-customer-modal" style={{ display: 'block' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-user-plus mr-2"></i>
              Attach Customer to Order #{orderNo}
            </h5>
            <button type="button" className="close" onClick={handleClose}>
              <span>&times;</span>
            </button>
          </div>

          <div className="modal-body">
            {!showConfirmation ? (
              <>
                <div className="form-group">
                  <label>Search for Customer:</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type customer name to search..."
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                    autoFocus
                  />
                </div>

                {results.length > 0 && (
                  <div className="customer-search-results" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <h6>Search Results:</h6>
                    {results.map((customer) => (
                      <div
                        key={customer.ID}
                        className="card mb-2 customer-result-card"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSelect(customer)}
                      >
                        <div className="card-body p-3">
                          <div className="row">
                            <div className="col-md-6">
                              <strong>
                                {customer.CustomerFullName ||
                                  `${customer.CustomerFirstName || ''} ${customer.CustomerLastName || ''}`.trim() ||
                                  'Unknown Customer'}
                              </strong>
                              <br />
                              <small className="text-muted">ID: {customer.ID}</small>
                            </div>
                            <div className="col-md-6">
                              <small>
                                {customer.CustomerPhone && <><i className="fas fa-phone"></i> {customer.CustomerPhone}<br /></>}
                                {customer.CustomerEmail && <><i className="fas fa-envelope"></i> {customer.CustomerEmail}<br /></>}
                                {customer.CustomerAddress && <><i className="fas fa-map-marker-alt"></i> {customer.CustomerAddress}</>}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {search.length >= 2 && results.length === 0 && (
                  <div className="alert alert-info">
                    No customers found matching "{search}"
                  </div>
                )}
              </>
            ) : (
              <div className="confirmation-section">
                <div className="alert alert-warning">
                  <h6><i className="fas fa-exclamation-triangle mr-2"></i>Confirm Customer Attachment</h6>
                  <p>Are you sure you want to attach the following customer to Order #{orderNo}?</p>
                  <div className="card">
                    <div className="card-body">
                      <h6>{selected?.customerName}</h6>
                      <p className="mb-1">
                        <strong>Customer ID:</strong> {selected?.ID}<br />
                        <strong>Phone:</strong> {selected?.phone || 'N/A'}<br />
                        <strong>Email:</strong> {selected?.email || 'N/A'}<br />
                        <strong>Address:</strong> {selected?.address || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 mb-0">
                    <small className="text-muted">
                      This will replace the current customer information on this order.
                    </small>
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            {!showConfirmation ? (
              <button type="button" className="btn btn-secondary" onClick={handleClose}>
                Cancel
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmation(false)}
                  disabled={isAttaching}
                >
                  Back to Search
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirm}
                  disabled={isAttaching}
                >
                  {isAttaching ? (
                    <><i className="fas fa-spinner fa-spin mr-2"></i>Attaching...</>
                  ) : (
                    <><i className="fas fa-check mr-2"></i>Confirm Attachment</>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachCustomerModal;
